import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProducts } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ProductForm } from "@/components/products/ProductForm";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import type { ProductFormData, ProductSize } from "@/types/product";

interface ProductOccasion {
  id: string;
  name: string;
}

export default function AdminEditProduct() {
  const navigate = useNavigate();
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { products, updateProduct: updateProductMutation, isLoading: productsLoading } = useProducts({
    storeId: storeId || "",
    enabled: !authLoading && !!user && !!isAdmin && !!storeId,
  });

  const product = products?.find(p => p.id === productId);

  // Fetch store name for display
  const { data: store } = useQuery({
    queryKey: ['admin-store-name', storeId],
    queryFn: async () => {
      if (!storeId) return null;
      const { data } = await supabase
        .from('store_profiles')
        .select('id, store_name')
        .eq('id', storeId)
        .single();
      return data;
    },
    enabled: !!storeId,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) {
      navigate("/");
    }
  }, [user, isAdmin, authLoading, navigate]);

  if (authLoading || productsLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!product) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900">Product Not Found</h1>
          <p className="mt-2 text-gray-600">The product you're looking for doesn't exist.</p>
          <Button
            onClick={() => navigate(`/admin/stores/${storeId}`)}
            className="mt-4"
          >
            Back to Store
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const handleSubmit = async (data: ProductFormData) => {
    try {
      setIsSubmitting(true);

      const invalidSize = data.sizes.find(size => !size.size_name || typeof size.price !== 'number' || size.price <= 0);
      if (invalidSize) {
        throw new Error("All sizes must have a name and valid price");
      }

      const mappedOccasions = data.occasions.map(name => {
        const existingOccasion = product.occasions.find((o: ProductOccasion) => o.name === name);
        return {
          id: existingOccasion?.id || "",
          name,
        };
      });

      const mainImageUrl = data.sizes[0]?.image_url || null;

      const result = await updateProductMutation({
        id: product.id,
        title: data.title,
        description: data.description,
        category: data.category,
        active: data.active,
        image_url: mainImageUrl,
        sizes: data.sizes.map(size => ({
          id: size.id,
          size_name: size.size_name,
          price: size.price,
          image_url: size.use_product_image ? null : size.image_url,
          use_product_image: !!size.use_product_image
        })),
        occasions: mappedOccasions,
      });

      toast.success("Product updated successfully");
      setIsSubmitting(false);
    } catch (error: any) {
      console.error("Error updating product:", error);
      setIsSubmitting(false);
      toast.error(error.message || "Failed to update product");
      throw error;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate(`/admin/stores/${storeId}`)}
            variant="ghost"
            className="gap-2 bg-[#967F81] hover:bg-[#843329]/10 hover:text-brand-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {store?.store_name || 'Store'}
          </Button>
        </div>

        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Edit Product</h1>
          <p className="text-muted-foreground mb-8">
            Editing product in <span className="font-medium">{store?.store_name || 'store'}</span>
          </p>

          {isSubmitting && (
            <div className="mb-4 p-2 bg-primary/10 rounded flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving product changes...</span>
            </div>
          )}

          <ProductForm
            onSubmit={handleSubmit}
            isLoading={isSubmitting}
            initialData={{
              id: product.id,
              title: product.title,
              description: product.description,
              category: product.category,
              active: product.active,
              occasions: product.occasions.map((o: ProductOccasion) => o.name),
              sizes: product.sizes.map((s: ProductSize, index: number) => {
                let imageUrl = null;
                if (index === 0) {
                  imageUrl = product.image_url || product.main_image_url || s.image_url || s.main_image_url || null;
                } else {
                  if (s.use_main_product_image || s.use_product_image) {
                    imageUrl = product.image_url || product.main_image_url || product.sizes[0]?.image_url || product.sizes[0]?.main_image_url || null;
                  } else {
                    imageUrl = s.image_url || s.main_image_url || null;
                  }
                }
                return {
                  id: s.id,
                  size_name: s.size_name || `Size ${index + 1}`,
                  price: s.price,
                  image_url: imageUrl,
                  use_product_image: s.use_main_product_image || s.use_product_image || false,
                };
              }),
            }}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
